import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCapabilities } from '../../components/admin/CapabilityProvider';
import { getModules } from '../../services/adminModulesService.js';
import AdminPageHeader from '../../components/admin/ui/AdminPageHeader';
import AdminCard from '../../components/admin/ui/AdminCard';
import AdminButton from '../../components/admin/ui/AdminButton';
import Icon from '../../components/admin/ui/icons/Icon';

const WORKSPACE_DEFINITIONS = [
  {
    id: 'analytics',
    devHubModuleKey: 'analytics',
    title: 'Analytics',
    description: 'Reporting and owner financials.',
    canonicalPath: '/modules/analytics',
    moduleCapability: 'admin:analytics:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'data',
  },
  {
    id: 'clc_properties',
    devHubModuleKey: 'property360',
    title: 'Property 360',
    description: 'Property and owner records.',
    canonicalPath: '/modules/clc-properties',
    moduleCapability: 'admin:clc-properties:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'home',
  },
  {
    id: 'daily_dashboard',
    devHubModuleKey: 'daily-dashboard-ez-care',
    title: 'Daily Dashboard',
    description: 'EZCare data, sync health, and daily operations.',
    canonicalPath: '/modules/daily-dashboard',
    moduleCapability: 'admin:daily-dashboard:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'data',
  },
  {
    id: 'serenity_chat',
    devHubModuleKey: 'serenity',
    title: 'Serenity',
    description: 'Your CLC concierge. Open the standalone development chat.',
    canonicalPath: 'https://serenity.clcvacation.com/',
    moduleCapability: 'admin:serenity:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'communications',
  },
  {
    id: 'serenity',
    devHubModuleKey: 'serenity-intelligence',
    title: 'Serenity Intelligence',
    description: 'Cross-app Serenity question intelligence.',
    canonicalPath: '/modules/serenity',
    moduleCapability: 'admin:serenity:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'data',
  },
  {
    id: 'booking_workspaces',
    devHubModuleKey: 'booking-workspaces',
    title: 'Booking Workspaces',
    description: 'Scoped third-party booking operations.',
    canonicalPath: '/modules/booking-workspaces',
    moduleCapability: 'admin:workspaces:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'users',
  },
  {
    id: 'development_hub',
    devHubModuleKey: 'development-hub',
    title: 'Development Hub',
    description: 'Plans, tasks, evidence, and verification.',
    canonicalPath: '/modules/development-hub',
    moduleCapability: 'admin:development-hub:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'work',
  },
  {
    id: 'clc_communications',
    devHubModuleKey: 'communications',
    title: 'CLC Communications',
    description: 'Queues, templates, and delivery history.',
    canonicalPath: '/modules/clc-communications/queue',
    moduleCapability: 'admin:clc-communications:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'communications',
  },
  {
    id: 'tax_review',
    devHubModuleKey: 'monthly-taxes',
    title: 'Tax Review',
    description: 'Monthly tax workflow.',
    canonicalPath: '/modules/tax-review',
    moduleCapability: 'admin:modules:read',
    legacyPath: null,
    legacyCapability: null,
    icon: 'audit',
  },
];

const SUPPORT_DEFINITIONS = [
  {
    id: 'module-governance',
    title: 'Module Governance',
    description: 'Readiness and build truth.',
    path: '/admin/modules',
    capability: 'admin:modules:read',
    actionLabel: 'Open Governance',
    icon: 'settings',
  },
  {
    id: 'integrations',
    title: 'Integrations',
    description: 'Hostaway and diagnostics.',
    path: '/admin/api-controller',
    capability: 'admin:integrations:read',
    actionLabel: 'Open Integrations',
    icon: 'integrations',
  },
  {
    id: 'deploy',
    title: 'Deploy',
    description: 'Release controls.',
    path: '/admin/deploy',
    superadminOnly: true,
    actionLabel: 'Open Deploy',
    icon: 'rocket',
  },
];

export default function DashboardPage() {
  const navigate = useNavigate();
  const { has, isSuperAdmin, loading: capsLoading } = useCapabilities();
  const [moduleData, setModuleData] = useState({
    modules: [],
    summary: null,
    runtimeBuild: null,
    loading: true,
    error: null,
  });
  const loadDashboardData = useCallback(async () => {
    if (has('admin:modules:read')) {
      try {
        const data = await getModules();
        setModuleData({
          modules: data.modules || [],
          summary: data.summary || null,
          runtimeBuild: data.runtime_build || null,
          loading: false,
          error: null,
        });
      } catch (err) {
        setModuleData({
          modules: [],
          summary: null,
          runtimeBuild: null,
          loading: false,
          error: err?.message || 'Failed to load module governance',
        });
      }
    } else {
      setModuleData({
        modules: [],
        summary: null,
        runtimeBuild: null,
        loading: false,
        error: null,
      });
    }

  }, [has]);

  useEffect(() => {
    if (capsLoading) return;
    loadDashboardData();
  }, [capsLoading, loadDashboardData]);

  const moduleMap = useMemo(() => {
    return new Map((moduleData.modules || []).map((module) => [module.module_id, module]));
  }, [moduleData.modules]);

  const workspaceCards = useMemo(() => {
    return WORKSPACE_DEFINITIONS
      .filter((workspace) => {
        const hasModuleAccess = workspace.moduleCapability ? has(workspace.moduleCapability) : true;
        const hasLegacyAccess = workspace.legacyCapability ? has(workspace.legacyCapability) : false;
        return hasModuleAccess || hasLegacyAccess;
      })
      .map((workspace) => {
        const governanceModule = moduleMap.get(workspace.id) || null;
        const moduleEntryPath = workspace.moduleCapability && has(workspace.moduleCapability)
          ? (governanceModule?.launch_path || workspace.canonicalPath)
          : null;
        const legacyEntryPath = workspace.legacyCapability && has(workspace.legacyCapability)
          ? workspace.legacyPath
          : null;

        return {
          ...workspace,
          governanceModule,
          moduleEntryPath,
          legacyEntryPath,
        };
      });
  }, [has, moduleMap]);

  const supportCards = useMemo(() => {
    return SUPPORT_DEFINITIONS.filter((item) => {
      if (item.superadminOnly && !isSuperAdmin) return false;
      if (!item.capability) return true;
      return has(item.capability);
    });
  }, [has, isSuperAdmin]);

  // This is a launcher count, not a claim about service health or readiness.
  const topLineItems = capsLoading ? ['Loading workspaces'] : [`${workspaceCards.length} workspaces`];
  const platformCards = supportCards.map(card => ({ ...card, meta: card.description }));

  return (
    <div className="command-center-shell">
      <AdminPageHeader
        title="Command Center"
        subtitle="Choose a workspace and move."
        showBreadcrumb={false}
        showBackToHome={false}
      />

      <div className="command-center-overview">
        <div className="command-center-topline" aria-label="Command Center summary">
          {topLineItems.map((item) => (
            <span key={item} className="command-center-topline-item">{item}</span>
          ))}
        </div>

        <div className="command-center-utility-strip" aria-label="Platform tools">
          {platformCards.map((card) => (
            <button
              key={card.id}
              type="button"
              className="command-center-utility-chip"
              onClick={() => navigate(card.path)}
            >
              <span className="command-center-utility-chip-icon">
                <Icon name={card.icon} size="sm" ariaLabel={card.title} />
              </span>
              <span className="command-center-utility-chip-copy">
                <span className="command-center-utility-chip-title">{card.title}</span>
                {card.meta ? (
                  <span className="command-center-utility-chip-meta">{card.meta}</span>
                ) : null}
              </span>
            </button>
          ))}
        </div>
      </div>

      <section className="command-center-section">
        <div className="command-center-section-kicker">Workspaces</div>
        <div className="admin-grid admin-grid-cols-3">
          {workspaceCards.map((workspace) => {
            const legacyLabel = workspace.legacyEntryPath ? 'Legacy Admin available' : null;

            return (
              <AdminCard key={workspace.id} density="compact" className="command-center-nav-card">
                <div className="command-center-nav-head">
                  <div className="command-center-nav-title-wrap">
                    <div className="command-center-nav-icon">
                      <Icon name={workspace.icon} size="md" ariaLabel={workspace.title} />
                    </div>
                    <div>
                      <div className="command-center-nav-title">{workspace.title}</div>
                      <div className="command-center-nav-description">{workspace.description}</div>
                    </div>
                  </div>

                </div>

                {legacyLabel ? <div className="command-center-nav-meta">{legacyLabel}</div> : null}

                <div className="command-center-nav-footer">
                  <div className="command-center-card-actions">
                    {workspace.moduleEntryPath && (
                      <a
                        className="admin-button-primary admin-button-size-sm"
                        style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                        href={workspace.moduleEntryPath}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`Open workspace: ${workspace.title} (opens in a new tab)`}
                      >
                        Open workspace
                      </a>
                    )}

                    {workspace.devHubModuleKey && has('admin:development-hub:read') && (
                      <a
                        className="admin-button-secondary admin-button-size-sm"
                        style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                        href={`/modules/development-hub/${encodeURIComponent(workspace.devHubModuleKey)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View in Dev Hub: ${workspace.title} (opens in a new tab)`}
                      >
                        View in Dev Hub
                      </a>
                    )}

                    {workspace.legacyEntryPath && (
                      <AdminButton
                        size="sm"
                        variant="ghost"
                        onClick={() => navigate(workspace.legacyEntryPath)}
                      >
                        Legacy Admin
                      </AdminButton>
                    )}
                  </div>
                </div>
              </AdminCard>
            );
          })}
        </div>
      </section>
    </div>
  );
}
